import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters, useFeatures } from '@/lib/hooks.js'
import {
  PageHeader,
  Segmented,
  Button,
  EmptyState,
  Skeleton,
  DataTable,
  type Column,
} from '@/ui'
import { StatCard } from '@/components/StatCard.js'
import { CashFlowChart } from '@/components/finance/CashFlowChart.js'
import { CategoryProfitChart } from '@/components/finance/CategoryProfitChart.js'
import { AdvancedOverview } from '@/components/finance/AdvancedOverview.js'
import type { FinancePeriod } from '@garage/shared'

/**
 * Finance page showing cash-basis or advanced P&L reporting depending on
 * the accounting mode setting. Dispatches to SimplifiedFinance or AdvancedOverview.
 *
 * @param embedded - When true, doesn't render its own PageHeader
 */
export function FinancePage({ embedded = false }: { embedded?: boolean } = {}) {
  const { advancedAccounting } = useFeatures()

  if (advancedAccounting) {
    return <AdvancedOverview embedded={embedded} />
  }

  return <SimplifiedFinance embedded={embedded} />
}

/**
 * Finance page showing cash-basis reporting: revenue, cost, profit over time.
 * All figures are cash received (when paid), not invoiced amounts.
 *
 * @param embedded - When true, doesn't render its own PageHeader
 */
function SimplifiedFinance({ embedded = false }: { embedded?: boolean } = {}) {
  const formatters = useFormatters()
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read months from URL, default to 12
  const monthsParam = searchParams.get('months')
  const months = monthsParam ? parseInt(monthsParam, 10) : 12

  const { data: report, isLoading, error, refetch } = useQuery({
    queryKey: qk.finance(months),
    queryFn: () => api.finance(months),
  })

  // Segmented control options
  const monthOptions = [
    { value: '1', label: t('finance.periods.thisMonth') },
    { value: '3', label: t('finance.periods.months', { count: 3 }) },
    { value: '6', label: t('finance.periods.months', { count: 6 }) },
    { value: '12', label: t('finance.periods.months', { count: 12 }) },
    { value: '24', label: t('finance.periods.months', { count: 24 }) },
  ]

  const handleMonthsChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('months', value)
    setSearchParams(newParams)
  }

  if (error) {
    return (
      <div className="space-y-8">
        {!embedded && (
          <PageHeader
            title={t('finance.title')}
            subtitle={t('finance.subtitle')}
          />
        )}
        <div className="flex items-center justify-center py-12">
          <EmptyState
            title={t('finance.failedToLoad')}
            description={t('common.pleaseTryAgain')}
            action={
              <Button variant="outlined" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  if (isLoading || !report) {
    return (
      <div className="space-y-8">
        {!embedded && (
          <PageHeader
            title={t('finance.title')}
            subtitle={t('finance.subtitle')}
          />
        )}
        <div className="flex items-center justify-between">
          <Segmented
            options={monthOptions}
            value={String(months)}
            onChange={handleMonthsChange}
            label={t('finance.timePeriod')}
            className="no-scrollbar"
          />
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-surface-container border border-outline-variant rounded-lg p-5"
            >
              <Skeleton lines={3} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const { summary, series, byCategory } = report

  // Table columns for monthly series
  const seriesColumns: Column<FinancePeriod>[] = [
    {
      key: 'period',
      header: t('finance.columns.month'),
      cell: (row) => {
        const parts = row.period.split('-')
        if (parts.length < 2 || !parts[0] || !parts[1]) return <div>{row.period}</div>
        const date = new Date(parseInt(parts[0]!), parseInt(parts[1]!) - 1, 1)
        const locale = i18n.language === 'ar' ? 'ar-u-nu-latn' : 'en-US'
        return <div>{date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}</div>
      },
      width: 'w-24',
    },
    {
      key: 'cashReceivedMinor',
      header: t('finance.columns.cashReceived'),
      cell: (row) => <div className="tabular">{formatters.amount(row.cashReceivedMinor)}</div>,
      numeric: true,
    },
    {
      key: 'revenueMinor',
      header: t('finance.columns.netRevenue'),
      cell: (row) => <div className="tabular">{formatters.amount(row.revenueMinor)}</div>,
      numeric: true,
    },
    {
      key: 'costMinor',
      header: t('finance.columns.cost'),
      cell: (row) => <div className="tabular">{formatters.amount(row.costMinor)}</div>,
      numeric: true,
    },
    {
      key: 'paymentFeesMinor',
      header: t('finance.columns.paymentFees'),
      cell: (row) => <div className="tabular">{formatters.amount(row.paymentFeesMinor)}</div>,
      numeric: true,
    },
    {
      key: 'profitMinor',
      header: t('finance.columns.profit'),
      cell: (row) => <div className="tabular">{formatters.amount(row.profitMinor)}</div>,
      numeric: true,
    },
    {
      key: 'marginPct',
      header: t('finance.columns.marginPct'),
      cell: (row) => <div className="tabular">{row.marginPct.toFixed(1)}%</div>,
      numeric: true,
    },
  ]

  return (
    <div className="space-y-8">
      {!embedded && (
        <PageHeader
          title={t('finance.title')}
          subtitle={t('finance.subtitle')}
        />
      )}

      {/* Time period control */}
      <div className="flex items-center justify-between">
        <Segmented
          options={monthOptions}
          value={String(months)}
          onChange={handleMonthsChange}
          label={t('finance.timePeriod')}
          className="no-scrollbar"
        />
      </div>

      {/* Stat cards: seven columns wrapping to multiple rows */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label={t('finance.columns.cashReceived')}
          value={formatters.money(summary.cashReceivedMinor)}
          meta={t('finance.paidCount', { count: summary.jobcardCount })}
        />
        <StatCard
          label={t('finance.columns.netRevenue')}
          value={formatters.money(summary.revenueMinor)}
        />
        <StatCard
          label={t('finance.columns.cost')}
          value={formatters.money(summary.costMinor)}
        />
        <StatCard
          label={t('finance.columns.paymentFees')}
          value={formatters.money(summary.paymentFeesMinor)}
        />
        <StatCard
          label={t('finance.columns.profit')}
          value={formatters.money(summary.profitMinor)}
        />
        <StatCard
          label={t('finance.columns.marginPct')}
          value={summary.marginPct.toFixed(1) + '%'}
        />
        <StatCard
          label={t('finance.outstanding')}
          value={formatters.money(summary.outstandingMinor)}
          breakdown={t('finance.outstandingBreakdown', { count: summary.outstandingCount })}
        />
      </div>

      {/* Honesty banner: show only when cost coverage is below 100% */}
      {summary.costCoveragePct < 100 && (
        <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
          <p className="text-body-sm text-on-surface">
            {t('finance.costCoverageWarning', { pct: summary.costCoveragePct.toFixed(0) })}
          </p>
        </div>
      )}

      {/* Charts section */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Cash flow chart */}
        <div className="bg-surface-container border border-outline-variant rounded-lg p-5 flex flex-col">
          <h3 className="text-title-md text-on-surface mb-4">{t('finance.cashFlow')}</h3>
          {series.length === 0 || series.every((s) => s.cashReceivedMinor === 0) ? (
            <EmptyState
              title={t('finance.noCashReceived')}
              description={t('finance.noCashReceivedDescription')}
            />
          ) : (
            <div className="flex-1 min-h-64">
              <CashFlowChart series={series} />
            </div>
          )}
        </div>

        {/* Category profit chart */}
        <div className="bg-surface-container border border-outline-variant rounded-lg p-5 flex flex-col">
          <h3 className="text-title-md text-on-surface mb-4">{t('finance.profitByCategory')}</h3>
          {byCategory.every((c) => c.profitMinor === 0) ? (
            <EmptyState
              title={t('finance.noProfit')}
              description={t('finance.noProfitDescription')}
            />
          ) : (
            <div className="flex-1 min-h-64">
              <CategoryProfitChart byCategory={byCategory} formatters={formatters} />
            </div>
          )}
        </div>
      </div>

      {/* Table view of monthly series */}
      <section className="space-y-4">
        <h2 className="text-title-md text-on-surface">{t('finance.monthlyBreakdown')}</h2>
        <DataTable
          columns={seriesColumns}
          rows={series}
          rowKey={(row) => row.period}
          stackOnMobile
          empty={
            <EmptyState
              title={t('finance.noData')}
              description={t('finance.noDataDescription')}
            />
          }
        />
      </section>
    </div>
  )
}
